import { useFieldArrayOf } from '@framework/form/form/relations/useFieldsArrayOf.ts';
import { t } from '@framework/i18n/intl/i18next.ts';
import { FlexRow } from '@framework/ui/components/row/FlexRow.tsx';
import { VerticalSpace } from '@framework/ui/components/space/VerticalSpace.tsx';
import clx from 'classnames';
import React, { useCallback, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { hasRichTextAdvanced } from '../../../../../inputs/rich-text-advanced/hasRichTextAdvanced.ts';
import { RichTextAdvancedRender } from '../../../../../inputs/rich-text-advanced/render/RichTextAdvancedRender.tsx';
import { ConfigFormItem } from '../../../../../ui/form/ConfigFormItem.tsx';
import { parseAdvancedRichTextToString } from '../../../../../validator/utils/parseAdvancedRichTextToString.ts';
import { WidgetScopeContextProvider } from '../../../../form/fillable/WidgetScopeContext.tsx';
import { type IWidgetComponentProps } from '../../../IWidgetType.ts';
import { getWidgetProps } from '../../inputs/common/util/getWidgetProps.ts';
import { AddRowButton } from './components/AddRowButton.tsx';
import { RemoveRowButton } from './components/RemoveRowButton.tsx';
import { useGetRepeatableChildrenDefault } from './hooks/useGetRepeatableChildrenDefault.ts';
import { type IRepeatableWidget } from './IRepeatableWidget.ts';
import { LabelLikeFormItem } from './style/LabelLikeFormItem.tsx';
import {
  AlignItemWrapper,
  RepeatableContainerWrapper,
  RowLastWrapper,
  RowParagraphWrapper,
  RowWrapper,
} from './style/RepeatableContainer.tsx';

export interface IRepeatableWidgetProps
  extends IWidgetComponentProps<IRepeatableWidget> {}

export function RepeatableWidgetComponent(props: IRepeatableWidgetProps) {
  const { widget } = props;
  const options = getWidgetProps(props);

  return (
    <ConfigFormItem
      name={options.name}
      onlyFirstLevelErrors
      readonly={options.readonly}
      widget={widget}
    >
      <RepeatableWidgetComponentContent {...props} />
    </ConfigFormItem>
  );
}

function RepeatableWidgetComponentContent(props: IRepeatableWidgetProps) {
  const { widget } = props;
  const options = getWidgetProps(props);

  const { register } = useFormContext();
  const [alertText, setAlertText] = useState<string | null>(null);

  // Necessario per far funzionare `resetField` in caso di visibilità
  // condizionale del campo
  register(options.name);
  const repeatableArrayField = useFieldArrayOf({ name: options.name });

  const labelText = parseAdvancedRichTextToString(widget.label);

  const handleRemove = useCallback(
    (index: number) => {
      repeatableArrayField.remove(index);
      setAlertText(
        t(
          'widgets.repeatable-content.alert-sr.removed',
          'Elemento {{index}} rimosso dalla lista {{labelText}}. Gli elementi successivi sono stati riordinati.',
          { labelText, index: index + 1 },
        ),
      );
    },
    [labelText, repeatableArrayField],
  );

  const getChildrenDefault = useGetRepeatableChildrenDefault(options.name);

  const handleAdd = useCallback(() => {
    const childrenDefault = getChildrenDefault();
    repeatableArrayField.append(childrenDefault);
    setAlertText(
      t(
        'widgets.repeatable-content.alert-sr.added',
        'Elemento {{index}} aggiunto alla lista {{labelText}}.',
        { labelText, index: repeatableArrayField.fields.length + 1 },
      ),
    );
  }, [getChildrenDefault, labelText, repeatableArrayField]);

  const hideAddButton = widget.layout?.hideAddButton;
  const showLastRow = !hideAddButton || hasRichTextAdvanced(widget.description);

  return (
    <RepeatableContainerWrapper
      className={clx({ 'hide-borders': widget.layout?.hideBorder })}
    >
      <LabelLikeFormItem
        label={widget?.label}
        required={widget.rules?.repeatableRange?.min > 0}
      />
      {widget.layout?.hideBorder && <VerticalSpace />}

      <div role="alert" style={{ height: 0, overflow: 'hidden' }}>
        {alertText}
      </div>

      {repeatableArrayField.fields.map((item, index) => (
        <RepeatableRow
          key={item.id}
          index={index}
          widget={widget}
          readonly={props.readonly}
          fieldCount={repeatableArrayField.fields.length}
          onRemove={handleRemove}
          prefix={`${options.name}.${index}.`}
        >
          {props.children}
        </RepeatableRow>
      ))}

      {showLastRow && (
        <RowLastWrapper
          className={clx({
            'no-list-to-show': repeatableArrayField.fields.length < 1,
          })}
        >
          <div>
            <FlexRow
              gutter={24}
              align="flex-start"
              right={
                !props.readonly && (
                  <AddRowButton
                    widget={widget}
                    itemCount={repeatableArrayField.fields.length}
                    onAdd={handleAdd}
                  />
                )
              }
            >
              {widget.description && (
                <RowParagraphWrapper>
                  <RichTextAdvancedRender
                    hasCustomFont
                    text={widget.description}
                  />
                </RowParagraphWrapper>
              )}
            </FlexRow>
          </div>
        </RowLastWrapper>
      )}
    </RepeatableContainerWrapper>
  );
}

interface IRepeatableRowProps {
  index: number;
  widget: IRepeatableWidget;
  readonly?: boolean;
  fieldCount: number;
  onRemove: (index: number) => void;
  prefix: string;
  children: React.ReactNode;
}

/**
 * Riga memoizzata del repeatable. Evita che le righe esistenti ri-rendino
 * quando viene aggiunta o rimossa una riga (il parent ri-rende ma le props
 * delle righe esistenti restano uguali).
 */
const RepeatableRow = React.memo(function RepeatableRow(
  props: IRepeatableRowProps,
) {
  const { index, widget, readonly, fieldCount, onRemove, prefix, children } =
    props;

  return (
    <RowWrapper className={clx({ 'first-element': index === 0 })}>
      <FlexRow
        gutter={12}
        left={
          widget.layout?.hideNumberItem ? undefined : (
            <AlignItemWrapper>{index + 1}</AlignItemWrapper>
          )
        }
        right={
          readonly || widget.layout?.hideRemoveButton ? undefined : (
            <RemoveRowButton
              widget={widget}
              index={index}
              onRemove={onRemove}
              itemCount={fieldCount}
            />
          )
        }
      >
        <WidgetScopeContextProvider prefix={prefix} listIndex={index}>
          {children}
        </WidgetScopeContextProvider>
      </FlexRow>
    </RowWrapper>
  );
});
